// Pure helpers between the composition package and the compose page: which
// rung this host can offer, the alternatives a word carries, the text the
// story reads as, and the ASR context the next dictation is sent with.
import {
  type CompositionEdit,
  type CompositionToken,
  type LadderRung,
  type LadderState,
  sentences,
  type WordToken,
} from "@bindery/composition";
import {
  MAX_TRANSCRIPTION_CONTEXT_CHARS,
  MAX_TRANSCRIPTION_CONTEXT_WORDS,
  promptWordCount,
} from "@bindery/speech/contract";
import type { DescribeWordResult } from "./describe-word-client";
import {
  DESCRIBE_MAX_DESCRIPTION_LENGTH,
  DESCRIBE_MAX_FAILED_LENGTH,
  DESCRIBE_MAX_SENTENCE_WORDS,
  DESCRIBE_MAX_WORD_LENGTH,
  type DescribeWordRequest,
  type DescribeWordResponse,
} from "../contracts/describe-word";

/** Rungs this page can render; a host adds `type` and `tell-me-about-it` by supplying a renderer. */
export const BUILT_IN_RUNGS: ReadonlySet<LadderRung> = new Set([
  "hear-and-compare",
  "choose",
  "re-say",
  "save-for-adult",
]);

export const MAX_ALTERNATIVES = 3;

export const hasAlternatives = (word: WordToken): boolean =>
  word.intent.some((j) => j.alternatives.length > 0);

const distinct = (word: WordToken, candidates: readonly string[]): string[] => {
  const seen = new Set([word.text.toLowerCase()]);
  return candidates
    .filter((w) => {
      const key = w.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, MAX_ALTERNATIVES);
};

/**
 * The words to offer by hearing: the newest recorded judgement's
 * alternatives, else the ones an unjudged intent call brought back (kept
 * by the page, never in the log, so an empty judgement hides no real one),
 * minus the word itself, at most three.
 */
export const alternativesOf = (
  word: WordToken,
  offered: readonly string[] = [],
): string[] => {
  const latest = [...word.intent]
    .reverse()
    .find((j) => j.alternatives.length > 0);
  return distinct(
    word,
    latest === undefined ? offered : latest.alternatives.map((a) => a.word),
  );
};

/**
 * What recognition heard for this word and the words it replaced, as the
 * child rejected them, newest first: the sound-alike signal "tell me about
 * it" adds to the description.
 */
export function failedTranscriptionsOf(
  edits: readonly CompositionEdit[],
  lineage: readonly string[],
  current: string,
  cap = 3,
): string[] {
  const ids = new Set(lineage);
  const heard = edits.flatMap((edit) => {
    const inserted =
      edit.op === "insert-dictation"
        ? edit.tokens
        : edit.op === "re-say" && edit.result.outcome === "replaced"
          ? edit.result.tokens
          : [];
    return inserted.flatMap((t) =>
      t.kind === "word" && ids.has(t.id) ? [t.text] : [],
    );
  });
  const seen = new Set<string>();
  return [current, ...heard.reverse()]
    .filter((text) => {
      const key = text.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, cap);
}

export interface DescribeGap {
  readonly sentence: readonly string[];
  readonly gapIndex: number;
}

/** The word's sentence without it, and where in that sentence it goes. */
export function describeGap(
  tokens: readonly CompositionToken[],
  tokenId: string,
): DescribeGap | null {
  const group = sentences(tokens).find((ids) => ids.includes(tokenId));
  if (group === undefined) return null;
  const byId = new Map(tokens.map((t) => [t.id, t] as const));
  const words = group.flatMap((id) => {
    const token = byId.get(id);
    return token?.kind === "word" ? [token] : [];
  });
  const gapIndex = words.findIndex((w) => w.id === tokenId);
  if (gapIndex < 0) return null;
  return {
    sentence: words.filter((w) => w.id !== tokenId).map((w) => w.text),
    gapIndex,
  };
}

/** At most `cap` of the sentence's words, the run nearest the gap, with the gap moved to match. */
export function gapWindow(gap: DescribeGap, cap: number): DescribeGap {
  const { sentence, gapIndex } = gap;
  if (sentence.length <= cap) return gap;
  const start = Math.max(
    0,
    Math.min(gapIndex - Math.floor(cap / 2), sentence.length - cap),
  );
  return {
    sentence: sentence.slice(start, start + cap),
    gapIndex: gapIndex - start,
  };
}

/** The leading whole words of `text` within `max` characters; a first word longer than that is cut. */
export function leadingWords(text: string, max: number): string {
  const words = text.split(/\s+/).filter((w) => w.length > 0);
  let kept = "";
  for (const word of words) {
    const next = kept === "" ? word : `${kept} ${word}`;
    if (next.length > max) break;
    kept = next;
  }
  return kept === "" ? (words[0] ?? "").slice(0, max) : kept;
}

/**
 * The describe-word request for this gap, held to the contract's limits.
 * A K–2 description ("and then… and then…") or a run-on sentence would
 * otherwise be refused on every try. The description keeps its start,
 * where the child says what the word is; the sentence keeps the words
 * nearest the gap. Null when nothing was said.
 */
export function describeRequest(
  gap: DescribeGap,
  failedTranscriptions: readonly string[],
  description: string,
): DescribeWordRequest | null {
  const said = leadingWords(description, DESCRIBE_MAX_DESCRIPTION_LENGTH);
  const failed = failedTranscriptions.flatMap((text) => {
    const kept = leadingWords(text, DESCRIBE_MAX_FAILED_LENGTH);
    return kept === "" ? [] : [kept];
  });
  if (said === "" || failed.length === 0) return null;
  // A token past the contract's word length is no real word, and cutting
  // it would invent one, so it is left out.
  const fits = (word: string) => word.length <= DESCRIBE_MAX_WORD_LENGTH;
  const window = gapWindow(
    {
      sentence: gap.sentence.filter(fits),
      gapIndex: gap.sentence.slice(0, gap.gapIndex).filter(fits).length,
    },
    DESCRIBE_MAX_SENTENCE_WORDS,
  );
  return {
    sentence: [...window.sentence],
    gapIndex: window.gapIndex,
    failedTranscriptions: failed,
    description: said,
  };
}

export type DescribeLanding =
  /** The word was confirmed, replaced or taken out meanwhile. */
  | { readonly kind: "drop" }
  /** The route was not reached or refused: the rung was not tried, so nothing is recorded. */
  | {
      readonly kind: "again";
      readonly reason: Exclude<DescribeWordResult["kind"], "answered">;
    }
  | {
      readonly kind: "offer";
      readonly candidates: readonly string[];
      readonly response: DescribeWordResponse;
    }
  /** The route answered with nothing: recorded as no-candidates, for a grown-up. */
  | { readonly kind: "nothing" };

/** What a describe-word answer does to a word, judged when it comes back. */
export function describeLanding(
  result: DescribeWordResult,
  word: CompositionToken | undefined,
): DescribeLanding {
  if (word?.kind !== "word" || word.resolution === "confirmed") {
    return { kind: "drop" };
  }
  if (result.kind !== "answered") return { kind: "again", reason: result.kind };
  const { response } = result;
  const candidates =
    response.outcome === "candidates"
      ? response.alternatives.map((a) => a.word)
      : [];
  return candidates.length > 0
    ? { kind: "offer", candidates, response }
    : { kind: "nothing" };
}

const DESCRIBE_LINE: Readonly<
  Record<Exclude<DescribeLanding["kind"], "drop">, string>
> = {
  again: "Hmm, I missed that. Tell me about it one more time?",
  offer: "Is one of these your word?",
  nothing: "That one is a puzzle. Let's save it for a grown-up.",
};

/** What the rung says aloud when the answer lands: nothing over a sheet the child has closed. */
export const describeLine = (
  landing: DescribeLanding,
  sheetOpen: boolean,
): string | null =>
  !sheetOpen || landing.kind === "drop" ? null : DESCRIBE_LINE[landing.kind];

/**
 * The rung to put in front of the child: the ladder's `next` when this host
 * renders it, else the first rung it does render among those still
 * available, else "save it for a grown-up", which every host has.
 */
export function offerRung(
  state: LadderState,
  supported: ReadonlySet<LadderRung>,
): LadderRung | null {
  if (state.kind !== "open") return null;
  if (supported.has(state.next)) return state.next;
  const after = state.available.indexOf(state.next);
  return (
    state.available.slice(after + 1).find((rung) => supported.has(rung)) ??
    "save-for-adult"
  );
}

/**
 * Whether the sheet offers "Yes, that's my word" beside the rung it shows:
 * only where the ladder lets the child confirm, and not twice.
 */
export const offersConfirm = (
  state: LadderState,
  offered: LadderRung | null,
): boolean =>
  state.kind === "open" &&
  state.available.includes("hear-and-compare") &&
  offered !== "hear-and-compare";

const SENTENCE_END = /[.!?]/;

const isClosingPunct = (text: string): boolean =>
  /^[.,!?;:…'’)\]]+$/.test(text);

export interface TextOptions {
  /** What an unresolved slot reads as; nothing by default. */
  readonly slot?: string;
}

/** Tokens as prose: words spaced, closing punctuation attached. */
export function storyText(
  tokens: readonly CompositionToken[],
  options: TextOptions = {},
): string {
  return tokens
    .reduce<string>((text, token) => {
      const piece =
        token.kind === "word"
          ? token.text
          : token.kind === "punct"
            ? token.text
            : (options.slot ?? "");
      if (piece === "") return text;
      const attach = token.kind === "punct" && isClosingPunct(token.text);
      return text === "" || attach ? text + piece : `${text} ${piece}`;
    }, "")
    .trim();
}

export type StoryPiece =
  | { readonly kind: "text"; readonly text: string }
  | { readonly kind: "blank" };

/**
 * Tokens as prose with each unresolved slot, and each word in `blanks`, kept
 * as a blank piece of its own, for a surface that draws the slot (print's
 * blank line). A text piece beside a blank carries the space between them,
 * so a blank at either edge of the sentence survives where `storyText`'s
 * trim would drop it.
 */
export function storyPieces(
  tokens: readonly CompositionToken[],
  blanks: ReadonlySet<string> = new Set(),
): StoryPiece[] {
  const pieces: StoryPiece[] = [];
  let text = "";
  const flush = () => {
    if (text !== "") pieces.push({ kind: "text", text });
    text = "";
  };
  for (const token of tokens) {
    if (token.kind === "unresolved" || blanks.has(token.id)) {
      if (text !== "") text += " ";
      flush();
      pieces.push({ kind: "blank" });
      continue;
    }
    const attach = token.kind === "punct" && isClosingPunct(token.text);
    const afterBlank =
      text === "" && pieces[pieces.length - 1]?.kind === "blank";
    text =
      text === ""
        ? afterBlank && !attach
          ? ` ${token.text}`
          : token.text
        : attach
          ? text + token.text
          : `${text} ${token.text}`;
  }
  flush();
  return pieces;
}

/** Whether the sentence closes itself; an open run is still being told. */
export const endsSentence = (tokens: readonly CompositionToken[]): boolean => {
  const last = tokens[tokens.length - 1];
  return last?.kind === "punct" && SENTENCE_END.test(last.text);
};

/**
 * The composition so far as Whisper `prompt` context, kept to the tail that
 * fits the contract's caps (Whisper reads only the end of a prompt anyway).
 */
export function transcriptionContext(
  tokens: readonly CompositionToken[],
): string {
  const words = storyText(tokens)
    .split(/\s+/)
    .filter((w) => w.length > 0);
  let kept = words.slice(-MAX_TRANSCRIPTION_CONTEXT_WORDS);
  while (
    kept.length > 0 &&
    (kept.join(" ").length > MAX_TRANSCRIPTION_CONTEXT_CHARS ||
      promptWordCount(kept.join(" ")) > MAX_TRANSCRIPTION_CONTEXT_WORDS)
  ) {
    kept = kept.slice(1);
  }
  return kept.join(" ");
}

/**
 * At most `cap` of `words`, the run that holds every judged id: the tail of
 * the story by default, moved back when a judged word (one inserted at a
 * cursor earlier in the text) would otherwise fall outside the window and
 * fail the request's rule that every judged id names one of its words.
 */
export function intentWindow<T extends { readonly id: string }>(
  words: readonly T[],
  judged: readonly string[],
  cap: number,
): T[] {
  if (words.length <= cap) return [...words];
  const ids = new Set(judged);
  const first = words.findIndex((w) => ids.has(w.id));
  const tail = words.length - cap;
  const start = first < 0 ? tail : Math.min(first, tail);
  return words.slice(start, start + cap);
}

/** The words a read-back highlights, in order, with the token each lights. */
export interface SpokenWord {
  readonly tokenId: string;
  readonly text: string;
}

export const spokenWords = (
  tokens: readonly CompositionToken[],
): SpokenWord[] =>
  tokens.flatMap((t) =>
    t.kind === "word" ? [{ tokenId: t.id, text: t.text }] : [],
  );
